// src/context/ConsentContext.js

import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { consentCategories } from "../utils/consentConfig";

// Initialisiert den dataLayer, falls nicht vorhanden
const initializeDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  // Setzt den Standard-Zustand für den Consent Mode v2 (alles abgelehnt)
  // Dies muss VOR dem GTM-Skript ausgeführt werden!
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    // Notwendige Cookies sind immer erlaubt
    functionality_storage: "granted",
    security_storage: "granted",
  });
};

const ConsentContext = createContext();

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null); // null: unentschieden, object: entschieden
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Initialisierung beim ersten Laden der App
  useEffect(() => {
    initializeDataLayer();
    const savedConsent = localStorage.getItem("cookie_consent");
    if (savedConsent) {
      const parsedConsent = JSON.parse(savedConsent);
      setConsent(parsedConsent);
      // Wichtig: Den gespeicherten Consent-Status erneut an GTM senden
      updateGtmConsent(parsedConsent);
    } else {
      // Wenn keine Entscheidung getroffen wurde, zeige direkt das Modal
      setIsModalOpen(true);
    }
  }, []);

  const updateGtmConsent = (currentConsent) => {
    console.log("Aktualisiere GTM Consent mit:", currentConsent);
    const consentState = {
      analytics_storage: currentConsent.analytics_storage
        ? "granted"
        : "denied",
      ad_storage: currentConsent.ad_storage ? "granted" : "denied",
      // Fügen Sie hier weitere Mappings hinzu, falls Sie mehr Kategorien haben
    };
    console.log("GTM Consent State:", consentState);

    // Definiere die gtag-Funktion hier lokal, um sicherzustellen, dass sie verfügbar ist.
    function gtag() {
      window.dataLayer.push(arguments);
    }

    // Rufe die gtag-Funktion mit der korrekten Syntax auf
    gtag("consent", "update", consentState);

    // Optional: Du kannst weiterhin ein benutzerdefiniertes Event pushen, wenn du
    // im GTM darauf reagieren möchtest.
    window.dataLayer.push({ event: "consent_update_finished" });
  };

  const updateConsent = useCallback(
    (newConsentSettings) => {
      // Verwende ein funktionales Update für setConsent
      setConsent((prevConsent) => {
        // `prevConsent` ist garantiert der aktuellste Wert des States
        const fullConsent = {
          ...prevConsent, // Nutze den vorherigen State
          ...newConsentSettings,
          necessary: true,
        };

        // Führe die Seiteneffekte innerhalb dieses Callbacks aus
        localStorage.setItem("cookie_consent", JSON.stringify(fullConsent));
        updateGtmConsent(fullConsent);

        return fullConsent; // Gib den neuen State zurück
      });

      // Diese können außerhalb bleiben, da sie nur die UI steuern
      setIsModalOpen(false);
    },
    [], // Die Abhängigkeiten können jetzt leer sein, da wir uns nicht mehr auf eine veraltete `consent`-Variable verlassen
  );

  const acceptAll = () => {
    const allConsents = consentCategories.reduce((acc, category) => {
      acc[category.id] = true;
      return acc;
    }, {});
    updateConsent(allConsents);
  };

  const declineAll = () => {
    const necessaryOnly = consentCategories.reduce((acc, category) => {
      acc[category.id] = !!category.isMandatory;
      return acc;
    }, {});
    updateConsent(necessaryOnly);
  };

  const openSettings = () => {
    setIsModalOpen(true);
  };

  const value = {
    consent,
    isModalOpen,
    setIsModalOpen,
    updateConsent,
    acceptAll,
    declineAll,
    consentCategories,
    openSettings,
  };

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
};

// Der Custom Hook, den wir in unseren Komponenten verwenden werden
export const useCookieConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error(
      "useCookieConsent muss innerhalb eines ConsentProvider verwendet werden.",
    );
  }
  return context;
};
